import { memo } from 'react';
import { Contrast, Palette, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { useGamificationSettings } from '@/hooks/useGamificationSettings';
import { cn } from '@/lib/utils';

export const AccessibilityQuickToggles = memo(() => {
  const { settings, updateSetting } = useGamificationSettings();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={cn(
            "h-9 w-9 hover:scale-110 active:scale-95 transition-transform duration-200",
            (settings.highContrastMode || settings.reduceMotion) && "text-primary"
          )}
        >
          <Contrast className="h-4 w-4" />
          <span className="sr-only">Accessibility settings</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Contrast className="h-3 w-3" />
          Quick Accessibility
        </DropdownMenuLabel>
        
        <DropdownMenuItem 
          className="cursor-pointer flex items-center justify-between py-3"
          onSelect={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-2">
            <Contrast className="h-4 w-4 text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">High Contrast</span>
              <span className="text-[10px] text-muted-foreground">Stronger borders & backgrounds</span>
            </div>
          </div>
          <Switch
            checked={settings.highContrastMode}
            onCheckedChange={(checked) => updateSetting('highContrastMode', checked)}
          />
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          className="cursor-pointer flex items-center justify-between py-3"
          onSelect={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">Mood Background</span>
              <span className="text-[10px] text-muted-foreground">Animated adaptive theme</span>
            </div>
          </div>
          <Switch
            checked={settings.moodAdaptiveTheme}
            onCheckedChange={(checked) => updateSetting('moodAdaptiveTheme', checked)}
          />
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          className="cursor-pointer flex items-center justify-between py-3"
          onSelect={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">Reduce Motion</span>
              <span className="text-[10px] text-muted-foreground">Disable animations</span>
            </div>
          </div>
          <Switch
            checked={settings.reduceMotion}
            onCheckedChange={(checked) => updateSetting('reduceMotion', checked)}
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

AccessibilityQuickToggles.displayName = 'AccessibilityQuickToggles';

export default AccessibilityQuickToggles;
